import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyDomainCreateMutation } from 'graphql-common';
import { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import { SelectOption } from '@lib/components/Select/types';
import toast from '@lib/components/Toast/Toast';
import { Values } from '@lib/interfaces/form';
import { ADD_DOMAIN_FIELD_NAME } from '../../CompanyDomains/utils/getAddDomainFormFields';
import { SsoIntegrationFieldNames } from './constants';

type UseAddDomainModalArgs = {
  formRef: React.RefObject<FormRef | undefined>;
};

export default function useAddDomainModal({ formRef }: UseAddDomainModalArgs) {
  const { t } = useTranslation();
  const [isDomainModalOpen, setDomainModalOpen] = useState(false);
  const [domainCacheKey, setDomainCacheKey] = useState(0);
  const openAddDomainModal = useCallback(() => setDomainModalOpen(true), []);
  const closeAddDomainModal = useCallback(() => setDomainModalOpen(false), []);

  const [domainCreateMutation, domainCreateMutationResult] =
    useCompanyDomainCreateMutation({
      onCompleted: (response) => {
        const domain = response.data;
        if (domain?.id && domain?.domain && formRef.current?.setValue) {
          const currentValues =
            (formRef.current.values?.[
              SsoIntegrationFieldNames.CompanyDomainIds
            ] as SelectOption[]) || [];
          formRef.current.setValue(
            SsoIntegrationFieldNames.CompanyDomainIds,
            [
              ...currentValues,
              { value: domain.id, label: domain.domain, data: domain },
            ],
            { shouldDirty: true, shouldValidate: true },
          );
          setDomainCacheKey((k) => k + 1);
        }
        closeAddDomainModal();
        toast({ content: t('domain-added-success') });
      },
      onError: (error) => {
        toast({ content: error.message || t('something-went-wrong') });
      },
    });

  const onSubmitAddDomain = useCallback(
    (values: Values) => {
      domainCreateMutation({
        variables: {
          attributes: { domain: values[ADD_DOMAIN_FIELD_NAME] as string },
        },
      });
    },
    [domainCreateMutation],
  );

  return {
    isDomainModalOpen,
    domainCacheKey,
    openAddDomainModal,
    closeAddDomainModal,
    onSubmitAddDomain,
    domainCreateMutationResult,
  };
}
